import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Spinner } from 'react-bootstrap';
import {
  SpecialMessageArtifact,
  BELOW_MESSAGE_ARTIFACT_TYPES,
} from './threadUtils';
import {
  CreateCanvasAssetRequest,
  GetProactivityCompilationResponse,
  CanvasComponentInfo,
} from '@dreamai/types';
import { ReactComponent as ArrowUpLinkIcon } from '../../../icons/arrow-up-link.svg';
import { ReactComponent as SidePanelIcon } from '../../../icons/side-panel.svg';
import { ReactComponent as ProactivityCheckIcon } from '../../../icons/proactivity-check.svg';
import { ReactComponent as StopIcon } from '../../../icons/stop.svg';
import api from '../../../api';
import './below-message-artifacts.scss';

interface BelowMessageArtifactsProps {
  specialMessageArtifacts: SpecialMessageArtifact[];
  threadId: string;
  colors?: Record<string, string>;
}

interface ProactivityCompilationData {
  id: string;
  title: string;
  subtitle: string;
  description?: string;
  externalLink?: string;
  canvasComponent?: CanvasComponentInfo;
}

interface ArtifactCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description?: string;
  onExternalLinkClick?: () => void;
  onCanvasClick?: () => void;
  hasExternalLink?: boolean;
  hasCanvas?: boolean;
  colors?: Record<string, string>;
  isProactivity?: boolean;
}

const ArtifactCard: React.FC<ArtifactCardProps> = ({
  icon,
  title,
  subtitle,
  description,
  onExternalLinkClick,
  onCanvasClick,
  hasExternalLink,
  hasCanvas,
  colors,
  isProactivity,
}) => {
  // Primary action: canvas takes precedence over external link
  const handleCardClick = () => {
    if (onCanvasClick) {
      onCanvasClick();
    } else if (onExternalLinkClick) {
      onExternalLinkClick();
    }
  };

  const isClickable = !!onCanvasClick || !!onExternalLinkClick;

  return (
    <div
      className={`below-message-artifact-card${isProactivity ? ' proactivity' : ''}${isClickable ? ' clickable' : ''}`}
      style={
        colors
          ? { background: colors.background, borderColor: colors.borderColor }
          : undefined
      }
      onClick={isClickable ? handleCardClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className='card-inner'>
        <div
          className={`card-header${description ? '' : ' no-description'}`}
          style={
            colors?.borderColor
              ? { borderColor: colors.borderColor }
              : undefined
          }
        >
          <div className='card-info'>
            <div
              className='card-icon'
              style={
                colors?.iconBackground
                  ? { backgroundColor: colors.iconBackground }
                  : undefined
              }
            >
              {icon}
            </div>
            <div className='card-titles'>
              <span className='card-title'>{title}</span>
              <span className='card-subtitle'>{subtitle}</span>
            </div>
          </div>
          <div className='card-actions'>
            {hasExternalLink && (
              <span className='card-action-icon'>
                <ArrowUpLinkIcon />
              </span>
            )}
            {hasCanvas && (
              <span className='card-action-icon'>
                <SidePanelIcon />
              </span>
            )}
          </div>
        </div>
        {description && <div className='card-description'>{description}</div>}
      </div>
    </div>
  );
};

const BelowMessageArtifacts: React.FC<BelowMessageArtifactsProps> = ({
  specialMessageArtifacts,
  threadId,
  colors,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [compilationData, setCompilationData] = useState<
    Record<string, ProactivityCompilationData>
  >({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  // Track which compilations have been fetched or are being fetched
  const fetchedRef = useRef<Set<string>>(new Set());

  // Filter to only below-message artifact types - memoize to prevent re-renders
  const filteredArtifacts = useMemo(
    () =>
      specialMessageArtifacts.filter((artifact) =>
        (BELOW_MESSAGE_ARTIFACT_TYPES as readonly string[]).includes(
          artifact.type,
        ),
      ),
    [specialMessageArtifacts],
  );

  // Extract compilation IDs to fetch - memoize for stable dependency
  const compilationIdsToFetch = useMemo(() => {
    return filteredArtifacts
      .filter(
        (artifact) =>
          artifact.type === 'proactivityCompilationId' &&
          artifact.data.proactivityCompilationId,
      )
      .map((artifact) => artifact.data.proactivityCompilationId as string);
  }, [filteredArtifacts]);

  // Fetch proactivity compilation data
  useEffect(() => {
    const fetchCompilationData = async (compilationId: string) => {
      // Skip if already fetched or currently fetching
      if (fetchedRef.current.has(compilationId)) return;
      fetchedRef.current.add(compilationId);

      setLoading((prev) => ({ ...prev, [compilationId]: true }));

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GetProactivityCompilationResponse>(
          `/proactivity/compilations/${compilationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data) {
          setCompilationData((prev) => ({
            ...prev,
            [compilationId]: {
              id: compilationId,
              title: response.data.title || 'Proactivity Update',
              subtitle: response.data.subtitle || 'Business Update',
              description: response.data.synthesizedPostChats || undefined,
              externalLink: response.data.externalLink,
              canvasComponent: response.data.canvasComponent,
            },
          }));
        }
      } catch (error) {
        console.error('Error fetching compilation data:', error);
        setErrors((prev) => ({ ...prev, [compilationId]: true }));
      } finally {
        setLoading((prev) => ({ ...prev, [compilationId]: false }));
      }
    };

    // Fetch data for all compilation IDs
    compilationIdsToFetch.forEach((compilationId) => {
      fetchCompilationData(compilationId);
    });
  }, [compilationIdsToFetch, getAccessTokenSilently]);

  // If no artifacts, don't render anything
  if (filteredArtifacts.length === 0) {
    return null;
  }

  const handleExternalLinkClick = (url: string) => {
    window.open(url, '_blank');
  };

  const handleCanvasClick = async (
    title: string,
    canvasComponent: CanvasComponentInfo,
  ) => {
    try {
      const token = await getAccessTokenSilently();
      const createRequest: CreateCanvasAssetRequest = {
        threadId,
        title,
        components: [
          {
            type: canvasComponent.type,
            props: canvasComponent.props,
            order: 1,
          },
        ],
      };

      await api.post('/canvas-assets', createRequest, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // Canvas system will automatically switch to the new asset via pusher events
    } catch (error) {
      console.error('Error creating canvas asset:', error);
    }
  };

  return (
    <div className='below-message-artifacts'>
      {filteredArtifacts.map((artifact, index) => {
        if (artifact.type === 'proactivityCompilationId') {
          const compilationId = artifact.data.proactivityCompilationId;
          if (!compilationId) return null;

          const data = compilationData[compilationId];
          const isLoading = loading[compilationId];

          if (isLoading) {
            return (
              <div
                key={index}
                className='below-message-artifact-card proactivity loading'
                style={
                  colors
                    ? {
                        background: colors.background,
                        borderColor: colors.borderColor,
                      }
                    : undefined
                }
              >
                <div className='card-inner'>
                  <div
                    className='card-header no-description'
                    style={
                      colors?.borderColor
                        ? { borderColor: colors.borderColor }
                        : undefined
                    }
                  >
                    <div className='card-info'>
                      <div
                        className='card-icon'
                        style={
                          colors?.iconBackground
                            ? { backgroundColor: colors.iconBackground }
                            : undefined
                        }
                      >
                        <Spinner size='sm' style={{ color: 'white' }} />
                      </div>
                      <div className='card-titles'>
                        <span
                          className='ghost-bar title'
                          style={
                            colors?.borderColor
                              ? { backgroundColor: colors.borderColor }
                              : undefined
                          }
                        />
                        <span
                          className='ghost-bar subtitle'
                          style={
                            colors?.borderColor
                              ? { backgroundColor: colors.borderColor }
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          const hasError = errors[compilationId];
          if (hasError) {
            return (
              <div key={index} className='below-message-artifact-card error'>
                <div className='card-header'>
                  <div className='card-icon'>
                    <StopIcon />
                  </div>
                  <div className='card-titles'>
                    <span className='card-title'>Failed to load</span>
                    <span className='card-subtitle'>
                      Something went wrong, please try again
                    </span>
                  </div>
                </div>
              </div>
            );
          }

          if (!data) return null;

          return (
            <ArtifactCard
              key={index}
              icon={<ProactivityCheckIcon />}
              title={data.title}
              subtitle={data.subtitle}
              description={data.description}
              hasExternalLink={!!data.externalLink}
              hasCanvas={!!data.canvasComponent}
              onExternalLinkClick={
                data.externalLink
                  ? () => handleExternalLinkClick(data.externalLink!)
                  : undefined
              }
              onCanvasClick={
                data.canvasComponent
                  ? () => handleCanvasClick(data.title, data.canvasComponent!)
                  : undefined
              }
              colors={colors}
              isProactivity
            />
          );
        }

        return null;
      })}
    </div>
  );
};

export default BelowMessageArtifacts;
